import { persist, restore, time } from 'App/utils/Helpers';
import { EventDispatcher } from 'App/utils/EventDispatcher';
import { i18n } from 'App/plugins/i18n';
import FilterFactory from './FiltersFactory';
import { CAT_PATENTS } from '@/app/utils/Collections';
import { isEmpty } from './Utils';
import { parseSearchStringState } from './searchStringState';

/**
 * @typedef {Object} FilterConf
 * @property {string} category
 * @property {Array<Object>} filters
 * @property {Array<function>} [resources]
 * @property {string} [searchBarProp]
 * @property {string} [useStrictSearch]
 */

/**
 * @class Filter
 *
 * @property {Array<FilterItem>} items
 */
export default class Filter extends EventDispatcher {
  static E_INIT = 'init';
  static E_CHANGE = 'change';
  static E_ACTIVATE = 'activate';
  static E_UPDATE_ITEM_VALUE = 'update:item-value';
  static E_ITEM_TOGGLE = 'item:toggle';
  static E_RESET = 'reset';

  /** @type {Store} */
  #store;
  /**
   * @type {boolean}
   */
  #initialized = false;
  /**
   * @type {boolean}
   */
  #initializing = false;
  /**
   * @type {FilterConf}
   */
  #config;
  /**
   * @type {Map}
   */
  #map;
  /**
   * @type {Set}
   */
  #activeItems;
  #naPropertyMap;
  /**
   * @type {boolean}
   */
  #active = false;

  #subCat = null;
  #isUserFilter = false;

  #mode = 'production';

  search = '';
  strict = false;
  isSpec = false;
  decodedFlavors = false;
  description = false;
  flavorClaims = false;
  chemicalFormula = false;
  claims = false;

  constructor(store, config, mode = 'production') {
    super();
    this.#mode = mode;
    this.#store = store;
    this.#config = config;
    this.#map = new Map();
    this.#naPropertyMap = new Map();
    this.#activeItems = new Set();
    this.items = [];
  }

  init = async (force) => {
    if (this.#initializing || (this.#initialized && !force)) {
      return true;
    }
    const factory = new FilterFactory();
    factory.addEventListener('updateValue', this.#onFilterItemUpdate);
    this.#initializing = true;
    const res = await this.loadResources();
    this.items = [...this.#config.filters, ...res].reduce((acc, conf) => {
      if (!this.#checkRoles(conf.roles)) {
        return acc;
      }
      if (
        this.#subCat === null ||
        !conf.categories ||
        conf.categories?.length === 0 ||
        conf.categories?.includes(this.#subCat)
      ) {
        try {
          const item = factory.createFilter(conf, this.#subCat);
          if (item.options.naProp) {
            this.#naPropertyMap.set(item.options.naProp, item.id);
          }
          this.#map.set(item.id, item);
          acc.push(item);
        } catch (err) {
          console.error(err);
        }
      }
      return acc;
    }, []);
    this.#initialized = true;
    this.#initializing = false;
    this.dispatchEvent({ type: Filter.E_INIT, target: this });
    return true;
  };

  #checkRoles(roles) {
    if (!roles?.length) {
      return true;
    }
    return roles.some((role) => this.#store.state.user.hasAccess(role));
  }

  #canUseNAFilters() {
    return Boolean(this.#store?.state?.flags?.isAdmin);
  }

  loadResources = async () => {
    if (this.#config.resources?.length) {
      const srcFetcher = async ({ name, loader }) => {
        if (typeof loader === 'function') {
          const now = time();
          const key = `wims-resources::${this.category}::${name}::${i18n.locale}`;
          const cached = restore(key, null);
          if (this.isProd && cached && cached.expireAt > now && cached.data?.length > 0) {
            return cached.data;
          }
          const resource = await loader();
          persist(key, { data: resource, expireAt: now + 3600 });
          return resource;
        }
        return Promise.resolve([]);
      };
      return Promise.all(this.#config.resources.map(srcFetcher)).then((results) =>
        results.reduce((acc, result) => [...acc, ...result], [])
      );
    }
    return Promise.resolve([]);
  };

  get mode() {
    return this.#mode;
  }

  get isProd() {
    return this.#mode === 'production';
  }

  get category() {
    return this.#config.category;
  }

  get initialized() {
    return this.#initialized;
  }

  /**
   * @param {string} id
   * @return FilterItem
   */
  getItem = (id) => {
    return this.#map.get(id);
  };

  activateItem = (id) => {
    const item = this.getItem(id);
    if (item) {
      item.activate();
      this.#activeItems.add(id);
      this.dispatchEvent({
        type: Filter.E_ITEM_TOGGLE,
        filter: this,
        state: true,
        item,
      });
      this.dispatchEvent({ type: Filter.E_CHANGE, filter: this, action: 'activate' });
    }
  };

  deactivateItem = (id) => {
    const item = this.getItem(id);
    if (item) {
      item.deactivate();
      item.resetValue();
      if (item.na && item.useNASearch) {
        item.resetNAValue();
      }
      this.#activeItems.delete(id);
      this.dispatchEvent({
        type: Filter.E_ITEM_TOGGLE,
        filter: this,
        state: false,
        item,
      });
      this.dispatchEvent({ type: Filter.E_CHANGE, filter: this, action: 'remove' });
    }
  };

  isItemActive = (id) => {
    return this.#activeItems.has(id);
  };

  getActiveAmount = () => {
    return this.#activeItems.size;
  };

  getActiveItems = () => {
    return [...this.#activeItems].map((id) => this.getItem(id));
  };

  getActiveItemsWithValues = () => {
    const activeItems = this.getActiveItems();

    const hasMeaningfulValue = (value) => {
      if (Array.isArray(value)) {
        return value.some((entry) => {
          if (typeof entry === 'string') {
            return entry.trim().length > 0;
          }
          return !isEmpty(entry);
        });
      }

      if (typeof value === 'string') {
        return value.trim().length > 0;
      }

      if (value && typeof value === 'object') {
        return Object.values(value).some((entry) => {
          if (typeof entry === 'string') {
            return entry.trim().length > 0;
          }
          if (Array.isArray(entry)) {
            return entry.some((nestedEntry) => {
              if (typeof nestedEntry === 'string') {
                return nestedEntry.trim().length > 0;
              }
              return !isEmpty(nestedEntry);
            });
          }
          return !isEmpty(entry);
        });
      }

      return !isEmpty(value);
    };

    return activeItems.filter((item) => {
      const collectedValues = item.collectValues(activeItems) || {};

      if (collectedValues?.__feature) {
        return hasMeaningfulValue(collectedValues.__feature);
      }

      return Object.entries(collectedValues).some(([key, value]) => {
        return !key.startsWith('__') && hasMeaningfulValue(value);
      });
    });
  };

  getActiveItemsWithValuesAmount = () => {
    return this.getActiveItemsWithValues().length;
  };

  setItemValue = (id, value) => {
    const item = this.getItem(id);
    if (item) {
      item.value = value;
    }
  };

  resetItemValue = (id) => {
    const item = this.getItem(id);
    if (item) {
      item.resetValue();
    }
  };

  deactivateAll = () => {
    this.items.forEach((item) => {
      item.deactivate();
      this.#activeItems.delete(item.id);
      item.resetValue();
      if (item.na && item.useNASearch) {
        item.resetNAValue();
      }
    });
    this.dispatchEvent({ type: Filter.E_RESET, filter: this });
    this.dispatchEvent({ type: Filter.E_CHANGE, filter: this });
  };

  activateAll = () => {
    this.items.forEach((item) => {
      item.activate();
      this.#activeItems.add(item.id);
    });
    this.dispatchEvent({ type: Filter.E_CHANGE, filter: this });
  };

  collectValues = async () => {
    const filters = this.getActiveItems();
    const parentFilters = filters.filter((filter) => !filter.options.parent);
    const childrenWithoutParent = filters.filter((cf) => {
      return cf.options.parent && !parentFilters.map((pf) => pf.id).includes(cf.options.parent);
    });

    let data = {};
    function getChildPropertyCalculator(collectedParent, parentProperty) {
      const lastItem = Object.keys(collectedParent).at(-1);

      const regex = /\[(\d+)\]/;
      const match = lastItem?.match(regex);
      // An active parent with no selected values collects nothing, so there is
      // no key to derive the property from — fall back to the parent's own.
      const propertyName = lastItem ? lastItem.replace(/\[\d+\]/, '') : parentProperty;
      let lastIndex = 0;
      if (match) {
        lastIndex = parseInt(match[1], 10);
      }
      return (offset) => {
        const propNumber = lastIndex + offset + 1;
        return `${propertyName}[${propNumber}]`;
      };
    }
    parentFilters.forEach((filter) => {
      const children = [];
      filters.forEach((f) => {
        if (f.options.parent == filter.id) {
          const collectedChild = f.collectValues();
          children.push(Object.entries(collectedChild).map(([, v]) => v));
        }
      });
      const collectedValues = filter.collectValues(filters);

      children.forEach((values) => {
        const propertyCalculator = getChildPropertyCalculator(collectedValues, filter.property);
        values.forEach((v, index) => {
          collectedValues[propertyCalculator(index)] = v;
        });
      });
      if (collectedValues?.__feature) {
        data[filter.id] = JSON.stringify(collectedValues.__feature);
        delete collectedValues.__feature;
      }

      // Sibling filters may share the same request `property` (e.g. two pickers both
      // mapping to `types.id`). Merge their array values instead of letting the last
      // one overwrite the rest; non-array collisions keep the previous last-wins.
      Object.entries(collectedValues).forEach(([key, value]) => {
        if (key in data && Array.isArray(data[key]) && Array.isArray(value)) {
          data[key] = [...new Set([...data[key], ...value])];
        } else {
          data[key] = value;
        }
      });
    });

    const groupedChildrenWithoutParent = {};
    childrenWithoutParent.forEach((filter) => {
      const propertyName = filter.propertyName || filter.property;
      if (!groupedChildrenWithoutParent[propertyName]) {
        groupedChildrenWithoutParent[propertyName] = [];
      }
      groupedChildrenWithoutParent[propertyName].push(filter);
    });

    Object.entries(groupedChildrenWithoutParent).forEach(([propertyName, filtersGroup]) => {
      let currentIndex = 1;

      const existingKeys = Object.keys(data).filter((key) => key.startsWith(propertyName + '['));
      if (existingKeys.length > 0) {
        const indices = existingKeys.map((key) => {
          const match = key.match(/\[(\d+)\]/);
          return match ? parseInt(match[1], 10) : 0;
        });
        currentIndex = Math.max(...indices) + 1;
      }

      filtersGroup.forEach((filter) => {
        const collectedValues = filter.collectValues(filters);
        if (collectedValues?.__feature) {
          data[filter.id] = JSON.stringify(collectedValues.__feature);
          delete collectedValues.__feature;
        }

        const reindexedValues = {};
        Object.entries(collectedValues).forEach(([key, value]) => {
          if (key.includes('[') && key.includes(']')) {
            const baseProperty = key.replace(/\[\d+\]/, '');
            const newKey = `${baseProperty}[${currentIndex}]`;
            reindexedValues[newKey] = value;
            currentIndex++;
          } else {
            reindexedValues[key] = value;
          }
        });

        data = { ...data, ...reindexedValues };
      });
    });

    return data;
  };

  collectValuesForRequest = async () => {
    const data = await this.collectValues();
    const activeItems = this.getActiveItems();

    if (!this.#canUseNAFilters()) {
      activeItems.forEach((item) => {
        if (item?.useNASearch && item?.options?.naProp) {
          delete data[item.options.naProp];
        }
      });
    }

    const featureItems = activeItems.filter((item) => item?.isFeature);

    let ungroupedFeatures = [];
    featureItems.forEach((item) => {
      const rawValue = data[item.id];
      if (rawValue === undefined || rawValue === null || rawValue === '') {
        return;
      }

      let parsed = rawValue;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch (e) {
          parsed = [];
        }
      }

      if (Array.isArray(parsed)) {
        ungroupedFeatures = [...ungroupedFeatures, ...parsed];
      } else if (parsed && typeof parsed === 'object') {
        ungroupedFeatures.push(parsed);
      }

      delete data[item.id];
    });

    if (ungroupedFeatures.length > 0) {
      data.features = JSON.stringify(ungroupedFeatures);
    }

    return data;
  };

  findItem = (id) => {
    let item = this.getItem(id);
    if (!item) {
      return this.items.find((el) => Object.values(el.strategyIds || {}).includes(id));
    }
    return item;
  };

  applyUserFilter = async (userFilter) => {
    const { searchString, conditions } = userFilter;
    const { ...rest } = conditions;

    const {
      parsedSearch,
      isStrict,
      isSpec,
      isDecodedFlavors,
      isDescription,
      isFlavorClaims,
      isChemicalFormula,
      isClaims,
    } = parseSearchStringState(searchString);

    this.#isUserFilter = true;
    await this.applyFilter(
      rest,
      parsedSearch,
      isStrict,
      isSpec,
      isDecodedFlavors,
      isDescription,
      isFlavorClaims,
      isChemicalFormula,
      isClaims
    );
  };

  /**
   * Applies conditions only. The search term and its modifiers live here next to the
   * conditions, so `applyFilter` would reset them — flows that just activate a condition
   * (a tag click in a listing table) must leave the search bar untouched.
   * @param {object} conditions
   */
  applyConditions = async (conditions) => {
    const { search, strict, isSpec, decodedFlavors, description, flavorClaims, chemicalFormula, claims } = this;
    await this.applyFilter(
      conditions,
      search,
      strict,
      isSpec,
      decodedFlavors,
      description,
      flavorClaims,
      chemicalFormula,
      claims
    );
  };

  applyFilter = async (
    conditions,
    searchString,
    strict = false,
    isSpec = false,
    isDecodedFlavors = false,
    isDescription = false,
    isFlavorClaims = false,
    isChemicalFormula = false,
    isClaims = false
  ) => {
    this.search = searchString;
    this.strict = strict;
    this.isSpec = isSpec;
    this.decodedFlavors = isDecodedFlavors;
    this.description = isDescription;
    this.flavorClaims = isFlavorClaims;
    this.chemicalFormula = isChemicalFormula;
    this.claims = isClaims;
    this.deactivateAll();
    const { __feature: rawSerializedFeatures, features: rawLegacyFeatures, ...filters } = conditions;
    const toFeatureEntries = (rawValue, featureItem = null) => {
      if (rawValue === undefined || rawValue === null || rawValue === '') {
        return [];
      }

      const mapScalarValuesToEntries = (scalarValues) => {
        if (!featureItem?.isFeature || !featureItem?.featureID) {
          return [];
        }
        const normalizedValues = scalarValues
          .flatMap((value) => (Array.isArray(value) ? value : [value]))
          .filter((value) => value !== undefined && value !== null && value !== '');

        if (!normalizedValues.length) {
          return [];
        }

        if (featureItem.id.startsWith('not_feature')) {
          return normalizedValues.map((value) => ({ [featureItem.featureID]: value, not: true }));
        }

        if (featureItem.type === 'condition-group') {
          return [{ [featureItem.featureID]: normalizedValues }];
        }

        return normalizedValues.map((value) => ({ [featureItem.featureID]: value }));
      };

      let parsed = rawValue;
      if (Array.isArray(parsed) && parsed.length === 1 && typeof parsed[0] === 'string') {
        parsed = parsed[0];
      }

      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch (e) {
          const scalarValues = Array.isArray(rawValue) ? rawValue : [rawValue];
          return mapScalarValuesToEntries(scalarValues);
        }
      }

      if (Array.isArray(parsed)) {
        const hasObjectEntries = parsed.some((entry) => entry && typeof entry === 'object' && !Array.isArray(entry));
        if (!hasObjectEntries) {
          return mapScalarValuesToEntries(parsed);
        }
        return parsed;
      }
      if (typeof parsed === 'object') {
        return [parsed];
      }
      return mapScalarValuesToEntries([parsed]);
    };

    let serializedFeatures = toFeatureEntries(rawSerializedFeatures ?? rawLegacyFeatures);
    const filtersMap = {};
    const directFeatureApplications = [];
    for (const [k, v] of Object.entries(filters || {})) {
      if (k.startsWith('__')) {
        continue;
      }
      const itemId = getItemId(k);
      const featureItem = this.findItem(itemId);
      const isDirectFeatureKey = itemId === k;
      if (featureItem?.isFeature && itemId === featureItem.id && isDirectFeatureKey) {
        const directEntries = toFeatureEntries(v, featureItem);
        if (directEntries.length > 0) {
          directFeatureApplications.push({ filter: featureItem, entries: directEntries });
        }
        continue;
      }
      let id = getItemId(k);
      // TODO: fix it
      id = id.replaceAll('[tokenized][or]', '[tokenized]').replaceAll('[tokenized][and]', '[tokenized]');
      if (id in filtersMap) {
        filtersMap[id].values.push(v);
        filtersMap[id].keyValueMap[k] = v;
      } else {
        filtersMap[id] = {
          values: [v],
          keyValueMap: {
            [k]: v,
          },
        };
      }
    }
    const childrenMap = {};
    const childPromises = [];
    for (let filterKey in filtersMap) {
      const filter = this.findItem(filterKey);
      if (!filter) {
        continue;
      }
      if (filter.options.children && filter.options.children.length) {
        filter.options.children.forEach((childID) => {
          const childrenItems = this.items.filter((f) => f.id == childID);
          childrenItems.forEach((child) => {
            const promise = new Promise((res) => {
              child.options.childOptions.childItems().then((result) => {
                const childItemsIDs = result.map((item) => item.value || item.id);
                childrenMap[filter.id] = {
                  childID: child.id,
                  items: childItemsIDs,
                };
                res();
              });
            });
            childPromises.push(promise);
          });
        });
      }
    }
    await Promise.all(childPromises);
    function isChildren([, v], childItems) {
      const values = Array.isArray(v) ? v : v === null || v === undefined || v === '' ? [] : [v];
      return values.every((item) => childItems.includes(item));
    }
    for (let key in filtersMap) {
      if (!childrenMap[key]) {
        continue;
      }
      const children = Object.entries(filtersMap[key].keyValueMap).filter((entry) =>
        isChildren(entry, childrenMap[key].items)
      );
      if (children.length === 0) {
        continue;
      }
      const parent = Object.entries(filtersMap[key].keyValueMap).filter(
        (entry) => !isChildren(entry, childrenMap[key].items)
      );
      filtersMap[key].keyValueMap = Object.fromEntries(parent);
      filtersMap[key].values = parent.map((entry) => entry[1]);
      const propName = childrenMap[getItemId(children[0][0])].childID;
      const preparedChildren = children.map(([k, v], index) => {
        const propName = childrenMap[getItemId(k)].childID;
        return [`${propName}[${index + 1}]`, v];
      });
      const childrenObject = Object.fromEntries(preparedChildren);
      filtersMap[propName] = {
        keyValueMap: {
          ...childrenObject,
        },
        values: Object.values(childrenObject),
      };
    }
    const promises = [];
    const activateAppliedFilter = (filter) => {
      if (filter.isEmpty) {
        filter.deactivate();
        this.#activeItems.delete(filter.id);
        return;
      }
      filter.activate();
      this.#activeItems.add(filter.id);
    };
    let features = [];
    if (serializedFeatures) {
      if (Array.isArray(serializedFeatures)) {
        features = serializedFeatures;
      } else if (typeof serializedFeatures === 'object') {
        features = [serializedFeatures];
      }
    }
    if (!Array.isArray(features)) {
      features = [features];
    }

    const featuresIds = new Set();
    features.forEach((f) => featuresIds.add(Object.keys(f)[0]));

    const groupedFeatures = [];
    const featuresIdsIterator = featuresIds.values();

    [...featuresIdsIterator].forEach((id) => {
      const group = features.filter((f) => id in f);
      groupedFeatures.push(group);
    });

    features = groupedFeatures;

    for (let filterKey in filtersMap) {
      let filter = this.findItem(filterKey);
      let shouldApplyNAState = false;
      if (filterKey.startsWith('exists')) {
        const naKey = Object.keys(filtersMap[filterKey].keyValueMap)[0];
        const filterID = this.#naPropertyMap.get(naKey);
        if (filterID) {
          filter = this.findItem(filterID);
          if (!this.#canUseNAFilters()) {
            if (filter?.useNASearch) {
              filter.na = false;
              filter.resetNAValue();
            }
            continue;
          }
          shouldApplyNAState =
            filter?.useNASearch && normalizeQueryBoolean(filtersMap[filterKey].values[0]) === Boolean(filter.inverseNA);
        }
      }
      if (!filter) {
        continue;
      }
      if (shouldApplyNAState) {
        filter.resetValue();
        filter.na = true;
        activateAppliedFilter(filter);
        continue;
      }
      if (filter.useNASearch) {
        filter.na = false;
      }
      const promise = new Promise((res, rej) => {
        filter
          .apply(filtersMap[filterKey].values, filtersMap[filterKey].keyValueMap)
          .then(() => {
            activateAppliedFilter(filter);
            res();
          })
          .catch((err) => rej(err));
      });
      promises.push(promise);
    }
    for (const { filter, entries } of directFeatureApplications) {
      const featureKey = String(filter.featureID || '');
      const mappedValues = entries
        .filter(isPlainObject)
        .map((entry) => {
          const key = getFeatureEntryKey(entry, featureKey);
          if (!key) {
            return [];
          }

          const rawValue = entry[key];

          if (filter?.type === 'condition-group') {
            const values = compactValueList(rawValue);
            return entry.not === true ? [...values, 'not'] : values;
          }

          return rawValue;
        })
        .filter((value) => !isEmpty(value));

      const promise = new Promise((res, rej) => {
        filter
          .apply(mappedValues, {}, entries)
          .then(() => {
            activateAppliedFilter(filter);
            res();
          })
          .catch((err) => rej(err));
      });
      promises.push(promise);
    }
    for (let i in features) {
      let values = features[i];
      if (!Array.isArray(values)) {
        values = [values];
      }
      if (!values.length) continue;
      const valueKeys = Object.keys(values[0]);
      const key = valueKeys[0];
      const not = valueKeys[1];
      let filterKey;
      if (values[0][key] && values[0][not]) {
        filterKey = `not_feature${key}`;
      } else {
        filterKey = `feature${key}`;
      }

      let filter = null;
      if (filterKey === 'feature95') {
        filter = this.items.find((f) => f.id === 'feature95_subcategory');
      } else if (filterKey === 'feature14') {
        filter = this.items.find((f) => f.id === 'filling_material');
      } else {
        filter = this.findItem(filterKey);
      }

      if (!filter) {
        continue;
      }
      const promise = new Promise((res, rej) => {
        const mappedValues = values.map((entry) => {
          const groupValue = entry?.[key];
          if (filter?.type === 'condition-group') {
            const normalizedGroup = compactValueList(groupValue);
            return entry?.not ? [...normalizedGroup, 'not'] : normalizedGroup;
          }
          return groupValue;
        });
        filter
          .apply(mappedValues, {}, values)
          .then(() => {
            activateAppliedFilter(filter);
            res();
          })
          .catch((err) => rej(err));
      });
      promises.push(promise);
    }
    try {
      await Promise.all(promises);
      this.dispatchEvent({ type: Filter.E_CHANGE, filter: this, apply: true });
    } catch (error) {
      console.error(error);
    }
  };

  #onFilterItemUpdate = ({ filterItem, value }) => {
    if (filterItem.isActive) {
      if (this.#isUserFilter) {
        this.#isUserFilter = false;
      }
    }
    this.dispatchEvent({
      type: Filter.E_UPDATE_ITEM_VALUE,
      filter: this,
      state: this.#active,
      item: filterItem,
      value,
    });
  };

  collectFilterData = async (data = {}) => {
    const { category, search, strict } = this;
    const searchString = strict && category !== CAT_PATENTS ? `"${search}"` : search;
    const conditions = await this.collectValues();
    return {
      category,
      searchString,
      conditions,
      ...data,
    };
  };

  get active() {
    return this.#active;
  }

  set active(state) {
    this.#active = !!state;
    this.dispatchEvent({ type: Filter.E_ACTIVATE, filter: this, state: this.#active });
  }

  get searchBarProp() {
    return this.#config.searchBarProp || 'name';
  }

  get useStrictSearch() {
    return this.#config.useStrictSearch;
  }

  get subCat() {
    return this.#subCat;
  }

  set subCat(value) {
    this.#subCat = value;
  }

  get isUserFilter() {
    return this.#isUserFilter;
  }
}

function getItemId(key) {
  if (key.includes('[tokenized]')) {
    return key;
  }
  if (/^feature?\d+$/.test(key)) {
    return key;
  }
  if (/^exists\[.+\]$/i.test(key)) {
    return key;
  }
  if (/^\d+$/.test(key)) {
    return `feature${key}`;
  }
  if (/^.+\[.*]$/i.test(key)) {
    return key.replace(/\[.*]$/gi, '');
  }
  return key;
}

function normalizeQueryBoolean(value) {
  if (typeof value === 'boolean') {
    return value;
  }

  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();

  if (['true', '1', 'yes'].includes(normalized)) {
    return true;
  }

  if (['false', '0', 'no'].includes(normalized)) {
    return false;
  }

  return null;
}

function isPlainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function compactValueList(value) {
  return (Array.isArray(value) ? value : [value]).filter((item) => !isEmpty(item));
}

function getFeatureEntryKey(entry, preferredKey) {
  if (preferredKey && preferredKey in entry) {
    return preferredKey;
  }

  return Object.keys(entry).find((key) => key !== 'not') || null;
}
